import { useState, useEffect, useRef } from "react";
import ObjectConfigApi from "../../../api/ObjectConfigApi";
import DataApi from "../../../api/DataApi";
import PicklistApi from "../../../api/PicklistApi";
import PubSub from "pubsub-js";
import {
  getFieldByName,
  getCompleteListViewFilters,
  isCompleteListViewFilter,
  validateListViewFilters,
} from "../../../utils/conditionBuilderUtils";
import { buildValueFieldsForObject } from "../../../utils/filterFieldValueUtils";

/** Same shape as LookupLayout — resolve display label from view API payload. */
function flattenViewRecordPayload(data) {
  if (!data || typeof data !== "object") return {};
  const out = {};
  const take = (flds) => {
    if (!Array.isArray(flds)) return;
    flds.forEach((f) => {
      if (!f) return;
      const name = f.fieldName || f.field_name;
      if (!name) return;
      const v = f.value !== undefined && f.value !== null ? f.value : f.displayValue;
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        out[name] = v;
      }
    });
  };
  if (Array.isArray(data.details)) {
    data.details.forEach((section) => take(section?.fields));
  }
  take(data.otherHiddenFields);
  return out;
}

function isUuidString(s) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(s || "").trim()
  );
}

async function enrichFiltersWithLookupLabels(filters, fields) {
  if (!filters?.length || !fields?.length) return filters;
  const out = [];
  for (const row of filters) {
    const fieldMeta = getFieldByName(fields, row.field, { nameKey: "field_name" });
    const isLookup =
      fieldMeta &&
      (fieldMeta.data_type === "lookup" ||
        fieldMeta.data_type === "reference" ||
        fieldMeta.is_reference === true) &&
      (fieldMeta.reference_object || fieldMeta.referenceObject);
    const vid = row.value != null && String(row.value).trim();
    if (!isLookup || !vid) {
      out.push(row);
      continue;
    }
    const labelStr = row.displayValue != null ? String(row.displayValue).trim() : "";
    const hasUsableLabel =
      labelStr &&
      !(isUuidString(labelStr) && labelStr === String(vid).trim());
    if (hasUsableLabel) {
      out.push(row);
      continue;
    }
    const refObj = fieldMeta.reference_object || fieldMeta.referenceObject;
    const displayFields = fieldMeta.display_fields || fieldMeta.displayFields || "name";
    const displayKeys = String(displayFields)
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);
    try {
      const dataApi = new DataApi(`object/view/${refObj}`);
      const res = await dataApi.fetchById(vid);
      const payload = res?.data?.data;
      const flat = flattenViewRecordPayload(payload);
      const label = displayKeys
        .map((key) => (flat[key] != null ? String(flat[key]).trim() : ""))
        .filter(Boolean)
        .join(" ");
      out.push(label ? { ...row, displayValue: label } : { ...row });
    } catch {
      out.push({ ...row });
    }
  }
  return out;
}

/**
 * Custom hook for managing list view filters
 */
export const useListViewFilters = (objectName, listViews, listViewName) => {
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filterPanelResourcesLoading, setFilterPanelResourcesLoading] = useState(false);
  const [externalFilters, setExternalFilters] = useState([]);
  const [externalFilterLogic, setExternalFilterLogic] = useState("AND");
  const [availableFields, setAvailableFields] = useState([]);
  const [filterValueFields, setFilterValueFields] = useState([]);
  const [picklistOptionsByFieldId, setPicklistOptionsByFieldId] = useState({});
  const filterPanelLoadSessionRef = useRef(0);
  const filterPanelWasOpenRef = useRef(false);

  useEffect(() => {
    if (filterPanelWasOpenRef.current && !filterPanelOpen) {
      filterPanelLoadSessionRef.current += 1;
      setFilterPanelResourcesLoading(false);
      setPicklistOptionsByFieldId({});
    }
    filterPanelWasOpenRef.current = filterPanelOpen;
  }, [filterPanelOpen]);

  // Sync filters when list view changes (skip while panel is open to avoid clobbering draft / enriched labels)
  useEffect(() => {
    if (filterPanelOpen) return;

    const currentListView = listViews.find(view => view.name === listViewName);
    if (currentListView?.config) {
      if (currentListView.config.filters && currentListView.config.filters.length > 0) {
        setExternalFilters(currentListView.config.filters);
      } else {
        setExternalFilters([]);
      }

      if (currentListView.config.logic) {
        setExternalFilterLogic(currentListView.config.logic);
      } else {
        setExternalFilterLogic("AND");
      }
    } else {
      setExternalFilters([]);
      setExternalFilterLogic("AND");
    }
  }, [listViews, listViewName, filterPanelOpen]);

  const toggleFilterPanel = async () => {
    if (!filterPanelOpen) {
      const loadSession = ++filterPanelLoadSessionRef.current;
      setFilterPanelResourcesLoading(true);
      setFilterPanelOpen(true);
      setPicklistOptionsByFieldId({});
      const stillActive = () => loadSession === filterPanelLoadSessionRef.current;
      try {
        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
        if (!stillActive()) return;
        const result = response.data;
        const fields = result?.fields?.length > 0 ? result.fields : [];
        setAvailableFields(fields);
        try {
          const { valueFields: vf } = await buildValueFieldsForObject(objectName);
          if (stillActive()) setFilterValueFields(vf?.length ? vf : fields);
        } catch {
          if (stillActive()) setFilterValueFields(fields);
        }

        const currentListView = listViews.find((view) => view.name === listViewName);
        let nextFilters = [];
        let nextLogic = "AND";
        if (currentListView?.config) {
          if (currentListView.config.filters?.length > 0) {
            nextFilters = currentListView.config.filters.map((f) => ({ ...f }));
          }
          if (currentListView.config.logic) {
            nextLogic = currentListView.config.logic;
          }
        }

        if (fields.length > 0) {
          const picklistApi = new PicklistApi();
          const picklistFields = fields.filter(
            (f) =>
              (f.data_type === "picklist" || f.data_type === "multipicklist") && f.id
          );
          const picklistEntries = await Promise.all(
            picklistFields.map(async (pf) => {
              try {
                const pr = await picklistApi.getActivePicklistValues(pf.id);
                const values = pr.data?.success ? pr.data.data || [] : [];
                const formattedValues = values.map((item) => ({
                  key: item.value_key,
                  value: item.value_label,
                }));
                return [pf.id, formattedValues];
              } catch {
                return [pf.id, []];
              }
            })
          );
          if (!stillActive()) return;
          setPicklistOptionsByFieldId(Object.fromEntries(picklistEntries));
        } else {
          if (!stillActive()) return;
          setPicklistOptionsByFieldId({});
        }

        const enriched =
          fields.length > 0 && nextFilters.length > 0
            ? await enrichFiltersWithLookupLabels(nextFilters, fields)
            : nextFilters;
        if (!stillActive()) return;
        setExternalFilters(enriched);
        setExternalFilterLogic(nextLogic);
      } catch (error) {
        console.error("Error fetching fields for filter:", error);
        if (!stillActive()) return;
        setAvailableFields([]);
        setPicklistOptionsByFieldId({});
      } finally {
        if (loadSession === filterPanelLoadSessionRef.current) {
          setFilterPanelResourcesLoading(false);
        }
      }
    } else {
      setFilterPanelOpen(false);
    }
  };

  const addExternalFilter = () => {
    const newFilter = {
      id: Date.now(),
      field: "",
      operator: "equals",
      value: "",
      valueType: "static",
    };
    setExternalFilters([...externalFilters, newFilter]);
  };

  const removeExternalFilter = (filterId) => {
    setExternalFilters(externalFilters.filter(f => f.id !== filterId));
  };

  const updateExternalFilter = (filterId, field, value) => {
    setExternalFilters((prev) =>
      prev.map((f) => (f.id === filterId ? { ...f, [field]: value } : f))
    );
  };

  const patchExternalFilter = (filterId, patch) => {
    setExternalFilters((prev) =>
      prev.map((f) => (f.id === filterId ? { ...f, ...patch } : f))
    );
  };

  const applyExternalFilters = async (setRefreshTrigger) => {
    try {
      const currentListView = listViews.find(view => view.name === listViewName);
      if (!currentListView) {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "No List View",
          message: "No list view selected. Please select a list view first."
        });
        return;
      }

      const filterErrors = validateListViewFilters(externalFilters);
      if (filterErrors.length > 0) {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Incomplete Filters",
          message: filterErrors[0],
        });
        return;
      }

      const validFilters = getCompleteListViewFilters(externalFilters);
      const updatedConfig = {
        ...currentListView.config,
        filters: validFilters,
        logic: externalFilterLogic
      };

      const configApi = new DataApi(`objectconfig/listviews/${objectName}`);
      const updateResponse = await configApi.update({
        config: updatedConfig
      }, currentListView.id);

      if (updateResponse?.data?.success) {
        setRefreshTrigger(prev => prev + 1);
        setFilterPanelOpen(false);
        PubSub.publish("RECORD_SAVED_TOAST", {
          title: "Filters Applied",
          message: `Filters applied successfully to "${currentListView.label || currentListView.name}"`
        });
      } else {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Filter Error",
          message: "Failed to save filters. Please try again."
        });
      }
    } catch (error) {
      console.error("Error applying filters:", error);
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Filter Error",
        message: "Error applying filters: " + (error.response?.data?.error || error.message)
      });
    }
  };

  const clearExternalFilters = async (setRefreshTrigger) => {
    try {
      const currentListView = listViews.find(view => view.name === listViewName);
      if (!currentListView) {
        setExternalFilters([]);
        setExternalFilterLogic("AND");
        return;
      }

      const updatedConfig = {
        ...currentListView.config,
        filters: [],
        logic: "AND"
      };

      const configApi = new DataApi(`objectconfig/listviews/${objectName}`);
      const updateResponse = await configApi.update({
        config: updatedConfig
      }, currentListView.id);

      if (updateResponse?.data?.success) {
        setExternalFilters([]);
        setExternalFilterLogic("AND");
        setRefreshTrigger(prev => prev + 1);
        PubSub.publish("RECORD_SAVED_TOAST", {
          title: "Filters Cleared",
          message: `Filters cleared from "${currentListView.label || currentListView.name}"`
        });
      } else {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Filter Error",
          message: "Failed to clear filters. Please try again."
        });
      }
    } catch (error) {
      console.error("Error clearing filters:", error);
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Filter Error",
        message: "Error clearing filters: " + (error.response?.data?.error || error.message)
      });
    }
  };

  // Badge / toolbar: reflect only saved list view filters, not in-panel draft edits (until Apply).
  const committedListView = listViews.find((view) => view.name === listViewName);
  const committedFilters = committedListView?.config?.filters ?? [];
  const activeFiltersCount = committedFilters.filter(isCompleteListViewFilter).length;
  const hasActiveFilters = activeFiltersCount > 0;

  return {
    filterPanelOpen,
    filterPanelResourcesLoading,
    picklistOptionsByFieldId,
    externalFilters,
    externalFilterLogic,
    availableFields,
    filterValueFields,
    activeFiltersCount,
    hasActiveFilters,
    toggleFilterPanel,
    addExternalFilter,
    removeExternalFilter,
    updateExternalFilter,
    patchExternalFilter,
    applyExternalFilters,
    clearExternalFilters,
    setExternalFilters,
    setExternalFilterLogic,
    setFilterPanelOpen
  };
};
